import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useMediaQuery } from './hooks/useMediaQuery'
import { slideToggle } from '@roghz/slidetoggle'

const ResourcesOptions = ({
  term,
  selectedValues,
  selectedFilters,
  validOptions,
  isOpen,
  onToggle,
  onChange,
  onClearFilter,
  onClearGroup,
  isLoading
}) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredTerms, setFilteredTerms] = useState(() => {
    if (Array.isArray(term.terms)) {
      return term.terms
    } else if (term.terms && typeof term.terms === 'object') {
      // Convert object with numeric keys to array
      return Object.values(term.terms)
    }
    return []
  })
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)

  useEffect(() => {
    if (Array.isArray(term.terms)) {
      setFilteredTerms(term.terms)
    } else if (term.terms && typeof term.terms === 'object') {
      // Convert object with numeric keys to array
      setFilteredTerms(Object.values(term.terms))
    } else {
      setFilteredTerms([])
    }
  }, [term.terms])

  const handleCheckboxChange = useCallback((value, checked) => {
    const newValues = checked
      ? [...selectedValues, value]
      : selectedValues.filter(v => v !== value)

    onChange(newValues)
    // Removed dropdown closing logic - dropdown stays open when selecting checkboxes
  }, [selectedValues, onChange])

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value)

    if (!value.trim()) {
      if (Array.isArray(term.terms)) {
        setFilteredTerms(term.terms)
      } else if (term.terms && typeof term.terms === 'object') {
        setFilteredTerms(Object.values(term.terms))
      } else {
        setFilteredTerms([])
      }
      return
    }

    let termsArray = []
    if (Array.isArray(term.terms)) {
      termsArray = term.terms
    } else if (term.terms && typeof term.terms === 'object') {
      termsArray = Object.values(term.terms)
    }
    const filtered = termsArray.filter(t =>
      t && t.name && t.name.toLowerCase().includes(value.toLowerCase())
    )

    setFilteredTerms(filtered)
  }, [term.terms])

  const handleClearSearch = useCallback(() => {
    setSearchTerm('')
    if (Array.isArray(term.terms)) {
      setFilteredTerms(term.terms)
    } else if (term.terms && typeof term.terms === 'object') {
      setFilteredTerms(Object.values(term.terms))
    } else {
      setFilteredTerms([])
    }
  }, [term.terms])

  const columnClass = useMemo(() => {
    const count = Array.isArray(filteredTerms) ? filteredTerms.length : 0

    if (term.is_author_profile) {
      return 'resources__options--list--col-4'
    } else if (count > 12) {
      return 'resources__options--list--col-3'
    } else if (count > 8) {
      return 'resources__options--list--col-2'
    }
    return ''
  }, [Array.isArray(filteredTerms) ? filteredTerms.length : 0, term.is_author_profile])

  // Function to adjust dropdown position to prevent overflow
  const adjustDropdownPosition = useCallback(() => {
    if (!dropdownRef.current || !buttonRef.current || isMobile) return

    const dropdown = dropdownRef.current
    const button = buttonRef.current
    const rect = dropdown.getBoundingClientRect()
    const buttonRect = button.getBoundingClientRect()
    const viewportWidth = window.innerWidth

    // Reset position first
    dropdown.style.left = '0'
    dropdown.style.right = 'auto'

    // Get the updated rect after reset
    const updatedRect = dropdown.getBoundingClientRect()

    // Check if dropdown extends beyond right edge
    if (updatedRect.right > viewportWidth) {
      const overflow = updatedRect.right - viewportWidth
      const newLeft = -overflow - 20 // Add 20px padding from edge

      // Make sure we don't go too far left (dropdown should still be visible)
      const maxLeft = -(updatedRect.width - buttonRect.width)
      const finalLeft = Math.max(newLeft, maxLeft)

      dropdown.style.left = `${finalLeft}px`
    }
  }, [isMobile])

  const handleToggle = useCallback(() => {
    if (isMobile) {
      // Mobile behavior - no animation, just toggle state
      onToggle()
    } else {
      // Desktop behavior - just toggle state, let CSS handle the display
      onToggle()
    }
  }, [isMobile, onToggle])

  // Adjust dropdown position when it opens
  useEffect(() => {
    if (isOpen && !isMobile) {
      // Use setTimeout to ensure the dropdown is rendered before calculating position
      const timeoutId = setTimeout(() => {
        adjustDropdownPosition()
      }, 0)

      return () => clearTimeout(timeoutId)
    }
  }, [isOpen, isMobile, adjustDropdownPosition])

  // Adjust position on window resize
  useEffect(() => {
    if (!isMobile) {
      const handleResize = () => {
        if (isOpen) {
          adjustDropdownPosition()
        }
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [isOpen, isMobile, adjustDropdownPosition])



  // Check if we have any terms to display
  let hasTerms = false
  if (Array.isArray(term.terms) && term.terms.length > 0) {
    hasTerms = true
  } else if (term.terms && typeof term.terms === 'object' && Object.keys(term.terms).length > 0) {
    hasTerms = true
  }

  if (!hasTerms) {
    return null
  }

  const isOptionDisabled = (option) => {
    // Only apply conditional disabling to author filters
    const isAuthorTerm = term.is_author_profile || term.term_name === 'author_profile' || term.name === 'author'

    if (!isAuthorTerm) {
      return false
    }

    // Get valid options for this term from the validOptions prop
    const validOptionsForTerm = Array.isArray(validOptions) ? validOptions : []

    // If no valid options provided, don't disable anything
    if (!validOptionsForTerm || validOptionsForTerm.length === 0) {
      return false
    }

    // Check if other terms have selections (excluding author and query)
    const otherTermsHaveSelections = Object.keys(selectedFilters || {}).some(key =>
      key !== 'author' && key !== 'query' && selectedFilters[key] && selectedFilters[key].length > 0
    )

    // Only disable authors if other filters are active
    if (!otherTermsHaveSelections) {
      return false
    }

    // Check if this option is in the valid options list
    const isDisabled = !validOptionsForTerm.includes(option.slug)

    return isDisabled
  }

  const handleClearGroup = useCallback(() => {
    // Call the parent's onClearGroup to properly update state
    // This should work without closing dropdowns since we're clearing values
    onClearGroup()
  }, [onClearGroup])

  return (
    <fieldset className={`resources__options--set ${term.name}`}>
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className={`resources__options--toggle ${isOpen ? 'is-active' : ''}`}
          aria-expanded={isOpen}
        >
          {term.label}
          {selectedValues.length > 0 && (
            <span className="resources__options--count">({selectedValues.length})</span>
          )}
        </button>

        <div
          ref={dropdownRef}
          className={`resources__options--dropdown ${isOpen ? 'is-active' : ''}`}
        >
          {term.is_author_profile && (
            <div className="resources__options--search">
              <input
                type="text"
                name="author-search"
                className="resources__options--search-input"
                placeholder="Search authors..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                aria-label="Search authors"
                data-search-type="author"
              />
              {searchTerm && (
                <button
                  className="resources__options--search-clear"
                  onClick={handleClearSearch}
                  aria-label="Clear author search"
                  type="button"
                >
                  &#10006;
                </button>
              )}
            </div>
          )}

          <ul className={`resources__options--list ${columnClass}`}>
            {Array.isArray(filteredTerms) ? filteredTerms.map((option) => {
              const isSelected = selectedValues.includes(option.slug)
              const isDisabled = isOptionDisabled(option)

              return (
                <li
                  key={option.term_id || option.slug}
                  className={`resources__options--item ${isDisabled ? 'is-disabled' : ''}`}
                  data-search={option.name.toLowerCase()}
                >
                  <label htmlFor={`${term.name}-${option.term_id || option.slug}`}>
                    <span className="resources__options--name" dangerouslySetInnerHTML={{__html: option.name}}></span>
                    <div className="resources__options--checkbox-wrapper">
                      <input
                        type="checkbox"
                        id={`${term.name}-${option.term_id || option.slug}`}
                        name={`${term.name}[]`}
                        value={option.slug}
                        checked={isSelected}
                        onChange={(e) => handleCheckboxChange(option.slug, e.target.checked)}
                        disabled={isDisabled}
                        className="resources__options--checkbox-input"
                      />
                      <span className={`resources__options--checkbox ${isSelected ? 'is-checked' : ''}`}>
                        {isSelected && (
                          <svg className="resources__options--checkbox-icon" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </span>
                    </div>
                  </label>
                </li>
              )
            }) : []}
          </ul>

          {Array.isArray(filteredTerms) && filteredTerms.length === 0 && searchTerm && (
            <div className="resources__options--no-results">
              No authors found matching "{searchTerm}"
            </div>
          )}

          {selectedValues.length > 0 && (
            <div className="resources__options--clear">
              <button
                onClick={handleClearGroup}
                className="resources__options--clear-btn"
                disabled={isLoading}
              >
                Clear {term.label}s
              </button>
            </div>
          )}
        </div>
    </fieldset>
  )
}

export default ResourcesOptions
